/**
 * @author Manifold Finance, Inc.
 * @date July, 12, 2023
 * @license GPL-3.0
 */

import { profile } from 'console';
import AccountStatus from '@/components/enFOLD/AccountStatus';
import Background from '@/components/enFOLD/Background';
import BigWhiteButton from '@/components/general/BigWhiteButton';
import {
  CardInnerContainer,
  CardInnerContainerMana,
  CardInnerContainerManaInvites,
  CardInnerContainerWithdrawals,
  CardOuterContainer,
  CardOuterContainerWithdrawals,
  HeaderContainer,
  LogoImage,
  SmallCube,
} from '@/components/general/Card';
import ExtraInfoPluStakeButton from '@/components/general/ExtraInfoPluStakeButton';
import StakeCenterFold from '@/components/mana/StakeCenterFold';
import StakeTopFold, { PossibleModes } from '@/components/mana/StakeTopFold';
import StakeCenterFoldManaSidebar from '@/components/manaSidebar/StakeCenterFold';
import StakeTopFoldManaSidebar from '@/components/manaSidebar/StakeTopFold';
import { pointsAtom, PointsDetails } from '@/components/manaStarter/StakeCenterFold';
import { NEXT_PUBLIC_URL } from '@/constants/addresses';
import useIsMobileMana from '@/hooks/useIsMobileMana';
import DefaultLayout from '@/layout/DefaultLayout';
import { supabase } from '@/lib/supabaseClient';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { signIn, signOut, useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { use, useEffect, useState } from 'react';
import styled from 'styled-components';
import { InvalidAbiItemError } from 'viem';
import { useAccount } from 'wagmi';
import { UserData, UserProfile } from '../api/invites/getCurrentProfile';
import { FilteredNewUser, Leader, Leaderboard } from '../api/invites/getLeaderboard';

function Header() {
  return (
    <HeaderContainer>
      <Link href="/">
        <LogoImage src="/svg/MEV Text White.svg" alt="mevETH" />
      </Link>
      <ExtraInfoPluStakeButton />
    </HeaderContainer>
  );
}
export const handleCopy = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    console.log('Text copied successfully!');
  } catch (err) {
    console.error('Failed to copy text:', err);
  }
};

function Card() {
  const isMobile = useIsMobileMana();
  const [points, setPoints] = useAtom(pointsAtom);
  const [mode, setMode] = useState<PossibleModes>('Withdrawals');
  const [showInvites, setShowInvites] = useState(false);
  const [showPools, setShowPools] = useState(false);
  const [showVotes, setShowVotes] = useState(false);
  const [user, setUser] = useState<UserProfile | null>();
  const [userName, setUserName] = useState('');
  const { address } = useAccount();
  const { data: session, status } = useSession();
  const [showEditProfile, setShowEditProfile] = useState(false);
  const router = useRouter();
  const [leaderboard, setLeaderboard] = useState<Leader[]>([]);
  const [newUsers, setNewUsers] = useState<FilteredNewUser[]>([]);
  useEffect(() => {
    const fetchLeaders = async () => {
      const leaderboard = await fetch('/api/invites/getLeaderboard');
      if (leaderboard.ok) {
        const data: Leaderboard = await leaderboard.json();
        setLeaderboard(data.data);
        setNewUsers(data.newUsers);
      }
    };
    fetchLeaders();
  }, []);

  // const [stakeMode, setStakeMode] = useState<"Ready" | "Loading" | "Convert">(
  const getUserName = () => {
    if (!user) return '';
    if (user.user_name) return user.user_name;
    if (user.first_name) return user.first_name;
    if (user.ens) return user.ens;
    if (user.metadata) return user.metadata.username;
    return 'anon';
  };
  const getUserAvatarUrl = () => {
    if (!user) return '';
    if (user.hide_avatar) return 'svg/anon.svg';
    if (user.avatar_url !== null && user.avatar_url) return user.avatar_url;
    if (user.twitter_image !== null && user.twitter_image) return user.twitter_image;

    return JSON.stringify(user.metadata);
  };
  if (isMobile)
    return (
      <LayoutGroup>
        <div style={{ marginTop: '100px' }}>
          <TopFrameMobile>
            <InsideFrame>
              <FrameColItemMobile
                style={{
                  width: '100%',
                  padding: '20px',
                }}
              >
                <TopFrameRowsMobile>
                  <FrameRowRecentJoinsMobile>
                    <div style={{ width: '100%' }}>
                      <div>
                        <FlexRowCenter
                          style={{
                            paddingBottom: '24px',
                            borderBottom: '1px solid white',
                          }}
                        >
                          <RecentJoins>Recent Joins</RecentJoins>
                        </FlexRowCenter>
                      </div>
                      <div
                        style={{
                          height: '800px',
                          overflowY: 'hidden',
                          overflowX: 'scroll',
                          scrollbarWidth: 'none',
                          //@ts-ignore
                          ':-webkit-scrollbar': {
                            display: 'none',
                          },
                          display: 'flex',
                          flexDirection: 'row',
                          width: '100%',
                        }}
                      >
                        {newUsers.slice(0, 20).map((newUser, index) => (
                          <FrameRowItemLeaderMobile key={index} style={{ width: '400px' }}>
                            <ItemInfoHeader style={{ flexBasis: '60%' }}>
                              <FlexRowCenter style={{ width: '250px', marginRight: '20px' }}>
                                <img
                                  src={newUser.profile_avatar_url || '/svg/anon.svg'}
                                  width={32}
                                  height={32}
                                  title={newUser.profile_user_name}
                                  style={{ marginRight: 10, borderRadius: '50px' }}
                                />
                                <div>
                                  {newUser.profile_user_name}
                                  <div style={{ fontWeight: 300, marginTop: '10px' }}>
                                    invited by @{newUser.from}{' '}
                                  </div>
                                </div>
                                <div style={{ marginLeft: 'auto', fontWeight: 300 }}>
                                  10 days ago
                                </div>
                              </FlexRowCenter>
                            </ItemInfoHeader>
                          </FrameRowItemLeaderMobile>
                        ))}
                      </div>
                    </div>
                  </FrameRowRecentJoinsMobile>
                  <div>
                    <div
                      style={{
                        paddingBottom: '24px',
                        borderBottom: '1px solid white',
                      }}
                    >
                      <RecentJoins>Leaderboard</RecentJoins>
                      <p style={{ marginTop: '10px' }}>{'Bridge & Invite friends to rank up'}</p>
                    </div>
                  </div>
                  <FrameRowItemHeaderMobile
                    style={{
                      borderTop: '1px solid white',
                      borderBottom: '1px solid white',
                      padding: '20px',
                    }}
                  >
                    <ItemInfoHeader style={{ flexBasis: '10%' }}>RANK</ItemInfoHeader>
                    <ItemInfoHeader style={{ flexBasis: '35%', paddingLeft: 0 }}>
                      NAME
                    </ItemInfoHeader>
                    <ItemInfoHeader>INVITED BY</ItemInfoHeader>
                    <ItemInfoHeader
                      style={{ textAlign: 'right', marginLeft: 'auto', flexBasis: '15%' }}
                    >
                      MANA
                    </ItemInfoHeader>
                  </FrameRowItemHeaderMobile>

                  <div
                    style={{
                      height: '400px',
                      overflowX: 'hidden',
                      overflowY: 'scroll',
                      scrollbarWidth: 'none',
                      //@ts-ignore
                      ':-webkit-scrollbar': {
                        display: 'none',
                      },
                    }}
                  >
                    {leaderboard.map((leader, index) => (
                      <FrameRowItemLeader style={{ width: '100%' }} key={index}>
                        <ItemInfoHeader style={{ flexBasis: '10%' }}>{index + 1}</ItemInfoHeader>
                        <ItemInfoHeader style={{ flexBasis: '35%' }}>
                          <FlexRowCenter>
                            <Image
                              src={leader.profile_avatar_url || '/svg/anon.svg'}
                              width={32}
                              height={32}
                              alt={leader.profile_user_name || 'anon'}
                              style={{ marginRight: 10 }}
                            />
                            {getUserName()}
                            {leader.profile_user_name}
                          </FlexRowCenter>
                        </ItemInfoHeader>
                        <ItemInfoHeader style={{ flexBasis: '40px' }}>
                          {leader.invitee_user_name}
                        </ItemInfoHeader>
                        <ItemInfoHeader
                          style={{ textAlign: 'right', marginLeft: 'auto', flexBasis: '15%' }}
                        >
                          {new Intl.NumberFormat('en-US').format(leader.total_points || 0)}
                        </ItemInfoHeader>
                      </FrameRowItemLeader>
                    ))}
                  </div>
                </TopFrameRowsMobile>{' '}
              </FrameColItemMobile>
            </InsideFrame>
          </TopFrameMobile>
        </div>
      </LayoutGroup>
    );
  return (
    <LayoutGroup>
      <div style={{ height: '1000px', margin: 'auto', marginTop: '200px' }}>
        <div style={{}}></div>
        {isMobile ? (
          <>
            <TopFrameMobile>
              <InsideFrame>
                <FrameColItemMobile
                  style={{
                    height: '1000px',
                    width: '100%',
                  }}
                >
                  <TopFrameRowsMobile>
                    <FrameRowItemHeaderMobile
                      style={{ borderTop: '1px solid white', borderBottom: '1px solid white' }}
                    >
                      <ItemInfoHeader style={{ flexBasis: '10%' }}>RANK</ItemInfoHeader>
                      <ItemInfoHeader style={{ flexBasis: '35%', paddingLeft: 0 }}>
                        NAME
                      </ItemInfoHeader>
                      <ItemInfoHeader>INVITED BY</ItemInfoHeader>
                      <ItemInfoHeader
                        style={{ textAlign: 'right', marginLeft: 'auto', flexBasis: '15%' }}
                      >
                        MANA
                      </ItemInfoHeader>
                    </FrameRowItemHeaderMobile>
                  </TopFrameRowsMobile>{' '}
                </FrameColItemMobile>

                {/*  <FrameRowRecentJoins>
                  <FrameCol>
                    <div>
                      <FlexRowCenter
                        style={{
                          paddingBottom: '24px',
                          borderBottom: '1px solid white',
                        }}
                      >
                        <RecentJoins>Recent Joins</RecentJoins>
                      </FlexRowCenter>
                    </div>

                    {leaderboard.slice(0, 20).map((leader, index) => (
                      <FrameRowItemLeader key={leader.id}>
                        <ItemInfoHeader style={{ flexBasis: '35%' }}>
                          <FlexRowCenter>
                            <Image
                              src={leader.profile_avatar_url || '/svg/anon.svg'}
                              width={32}
                              height={32}
                              alt={leader.profile_user_name}
                              style={{ marginRight: 10 }}
                            />
                            <div>
                              {leader.profile_user_name}
                              <div style={{ fontWeight: 300, marginTop: '10px' }}>
                                invited by @{leader.invitee_user_name}{' '}
                              </div>
                            </div>
                            <div style={{ marginLeft: 'auto', fontWeight: 300 }}>10 days ago</div>
                          </FlexRowCenter>
                        </ItemInfoHeader>
                      </FrameRowItemLeader>
                    ))}
                  </FrameCol>
                </FrameRowRecentJoins>*/}
              </InsideFrame>
            </TopFrameMobile>
          </>
        ) : (
          <>
            <TopFrame>
              <InsideFrame>
                <FrameCol
                  style={{
                    width: '886px',
                    height: '1000px',
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                    scrollbarWidth: 'none',
                    //@ts-ignore
                    ':-webkit-scrollbar': {
                      display: 'none',
                    },
                  }}
                >
                  <FrameRow>
                    <FrameRowLeaderboard>
                      <FrameRowBigTitle>Leaderboard</FrameRowBigTitle>
                    </FrameRowLeaderboard>
                    <FrameRowLeaderboard style={{ marginLeft: 'auto' }}>
                      <FrameRowBigSubTitle>
                        {'Bridge & Invite friends to rank up'}
                      </FrameRowBigSubTitle>
                    </FrameRowLeaderboard>
                  </FrameRow>
                  <TopFrameRows>
                    <FrameRowItemHeader
                      style={{ borderTop: '1px solid white', borderBottom: '1px solid white' }}
                    >
                      <ItemInfoHeader style={{ flexBasis: '10%' }}>RANK</ItemInfoHeader>
                      <ItemInfoHeader style={{ flexBasis: '35%', paddingLeft: 0 }}>
                        NAME
                      </ItemInfoHeader>
                      <ItemInfoHeader>INVITED BY</ItemInfoHeader>
                      <ItemInfoHeader
                        style={{ textAlign: 'right', marginLeft: 'auto', flexBasis: '15%' }}
                      >
                        MANA
                      </ItemInfoHeader>
                    </FrameRowItemHeader>
                    {leaderboard.map((leader, index) => (
                      <FrameRowItemLeader key={index}>
                        <ItemInfoHeader style={{ flexBasis: '10%' }}>{index + 1}</ItemInfoHeader>
                        <ItemInfoHeader style={{ flexBasis: '35%' }}>
                          <FlexRowCenter>
                            <Image
                              src={leader.profile_avatar_url || '/svg/anon.svg'}
                              width={32}
                              height={32}
                              alt={leader.profile_user_name || 'anon'}
                              style={{ marginRight: 10 }}
                            />
                            {getUserName()}
                            {leader.profile_user_name}
                          </FlexRowCenter>
                        </ItemInfoHeader>
                        <ItemInfoHeader style={{ flexBasis: '40px' }}>
                          {leader.invitee_user_name}
                        </ItemInfoHeader>
                        <ItemInfoHeader
                          style={{ textAlign: 'right', marginLeft: 'auto', flexBasis: '15%' }}
                        >
                          {new Intl.NumberFormat('en-US').format(leader.total_points || 0)}
                        </ItemInfoHeader>
                      </FrameRowItemLeader>
                    ))}
                  </TopFrameRows>{' '}
                </FrameCol>

                <FrameRowRecentJoins>
                  <FrameCol>
                    <div>
                      <FlexRowCenter
                        style={{
                          paddingBottom: '24px',
                          borderBottom: '1px solid white',
                        }}
                      >
                        <RecentJoins>Recent Joins</RecentJoins>
                      </FlexRowCenter>
                    </div>

                    {newUsers.slice(0, 10).map((newUser, index) => (
                      <FrameRowItemLeader key={index}>
                        <ItemInfoHeader style={{ flexBasis: '35%' }}>
                          <FlexRowCenter>
                            <img
                              src={newUser.profile_avatar_url || '/svg/anon.svg'}
                              width={32}
                              height={32}
                              alt={newUser.profile_user_name}
                              style={{ marginRight: 10, borderRadius: '50px' }}
                            />
                            <div>
                              {newUser.profile_user_name}
                              <div style={{ fontWeight: 300, marginTop: '10px' }}>
                                invited by @{newUser.from}{' '}
                              </div>
                            </div>
                            <div style={{ marginLeft: 'auto', fontWeight: 300 }}>10 days ago</div>
                          </FlexRowCenter>
                        </ItemInfoHeader>
                      </FrameRowItemLeader>
                    ))}
                  </FrameCol>
                </FrameRowRecentJoins>
              </InsideFrame>
            </TopFrame>
          </>
        )}

        <AccountStatus />
        {/*     <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <CardInnerContainerMana>
            <AnimatePresence>
              <StakeCenterFoldManaSidebar mode={mode} setMode={setMode} />
            </AnimatePresence>
          </CardInnerContainerMana>

          <CardInnerContainerManaInvites style={{ height: 700 }}>
            <StakeTopFold mode={mode} setMode={setMode} />
            <AnimatePresence>
              <StakeCenterFold mode={mode} setMode={setMode} />
            </AnimatePresence>
          </CardInnerContainerManaInvites>
        </div>*/}
      </div>
    </LayoutGroup>
  );
}

export default function StakePage() {
  return (
    <DefaultLayout>
      <Header />
      <Card />
      <Background />
    </DefaultLayout>
  );
}

const TopFrame = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-center;
  padding: 0px;
  gap: 24px;

  width: 1372px;
  height: 158px;
`;

const TopFrameMobile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-center;
  padding: 4px;
  gap: 24px;

  width: 100%;
`;

const InsideFrame = styled.div`
  display: flex;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
`;

const FrameRow = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  width: 886px;
  height: 158px;
  margin-right: 30px;
`;

const FrameRowLeaderboard = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  margin-right: 30px;
`;

const FrameCol = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  width: 886px;
  height: 158px;
  margin-right: 60px;
`;

const FrameRowRecentJoins = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  width: 446px;
  height: 158px;
  margin-right: 60px;
`;

const FrameRowRecentJoinsMobile = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  height: 158px;
`;
const FrameRowMobile = styled.div`
  /* Auto layout */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px;
  gap: 16px;

  width: 100%;

  background: rgba(79, 79, 79, 0.23);
  border: 1px solid rgba(79, 79, 79, 0.19);
  backdrop-filter: blur(6px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 10px;
`;
const FrameRowBigTitle = styled.div`
  font-size: 48px;
  font-weight: 600;
  margin-bottom: -10px;
`;
const FrameRowBigSubTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  margin-left: auto;
  padding-top: 24px;
`;

const FrameRowBigTitleMobile = styled.div`
  font-size: 24px;
  font-weight: 600;
`;

const FrameRowSubTitle = styled.div`
  margin-top: 16px;
`;

const FrameRowManaBalanceTitle = styled.div`
  font-size: 28px;
`;

const FlexRowCenter = styled.div`
  display: flex;

  flex-direction: row;

  align-items: center;
`;

const RecentJoins = styled.h2`
  font-size: 30px;
  font-weight: 600;
`;

const ManaBalance = styled.div`
  font-size: 24px;
  font-weight: 600;
  margin-top: 10px;
`;
const ManaBalanceLabel = styled.div`
  margin-top: -5px;
  font-weight: 500;
`;
const ManaUserOptions = styled.div`
  font-size: 24px;
  font-weight: 600;
  margin-top: 10px;

  margin-left: 50px;
`;
const ManaUserName = styled.div`
  margin-top: -5px;
  font-weight: 500;
  margin-left: 50px;
`;

const TopFrameRows = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-center;
  padding: 0px;
  margin-top: 24px;
`;
const TopFrameRowsMobile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-center;
  padding: 0px;
  margin-top: 24px;
  width: 100%;
`;
const FrameRowItem = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 32px;
  gap: 16px;

  width: 1296px;
  min-height: 74px;
  margin-bottom: 15px;
  background: #12151c;
  border: 1px solid rgba(79, 79, 79, 0.19);
  backdrop-filter: blur(6px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 10px;
`;

const FrameRowItemMobile = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  align-items: flex-start;
`;

const FrameRowItemMobileContainer = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  gap: 16px;

  width: 100%;
  min-height: 74px;
  margin-bottom: 15px;
  background: #12151c;
  border: 1px solid rgba(79, 79, 79, 0.19);
  backdrop-filter: blur(6px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 10px;
`;

const FrameColItemMobile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const FrameRowItemChildren = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 20px 12px 12px 12px;
  gap: 16px;

  width: 1236px;
  min-height: 54px;
  margin-bottom: 15px;
  margin-left: 50px;
  background: #12151c;
  border: 1px solid rgba(79, 79, 79, 0.19);
  backdrop-filter: blur(6px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 10px;
`;

const FrameRowItemChildrenMobile = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 20px 12px 12px 12px;
  gap: 16px;

  width: 50%;
  min-height: 54px;
  margin-bottom: 15px;
  margin-left: 50px;
`;

const FrameRowItemHeader = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-top: 20px;
  padding-bottom: 20px;
  gap: 16px;

  width: 886px;
  font-weight: 600;
  /* Note: backdrop-filter has minimal browser support */
`;

const FrameRowItemHeaderMobile = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-top: 20px;
  padding-bottom: 20px;
  gap: 16px;

  font-weight: 600;
  /* Note: backdrop-filter has minimal browser support */
`;

const FrameRowItemLeader = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-top: 20px;
  padding-bottom: 20px;
  gap: 16px;

  width: 886px;
  min-height: 74px;
  /* Note: backdrop-filter has minimal browser support */
`;

const FrameRowItemLeaderMobile = styled.div`
  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-top: 20px;
  padding-bottom: 20px;

  width: 100%;
  min-height: 74px;
  /* Note: backdrop-filter has minimal browser support */
`;

const ItemInviteCode = styled.div`
  flex-basis: 31%;
`;
const ItemCheckbox = styled.div`
  flex-basis: 1%;
  height: 32px;
`;
const ItemLabel = styled.div`
  flex-basis: 30%;
`;
const ItemLabelMobile = styled.div`
  flex-basis: 99%;
`;
const ItemLabelHeader = styled.div`
  flex-basis: 31%;
  font-weight: 500;
  font-size: 14px;
`;
const ItemLabelTitle = styled.h2`
  font-weight: 600;
  font-size: 16px;
`;
const ItemInfo = styled.div`
  flex-basis: 50%;
  font-size: 14px;
  padding: 0;
`;
const ItemInfoHeader = styled.div`
  flex-basis: 20%;
  padding: 0;
  font-weight: 600;
  font-size: 14px;
`;
const ItemInfoLeader = styled.div`
  flex-basis: 20%;
  padding: 0;
  font-weight: 500;
  font-size: 14px;
`;
const ItemAction = styled.div`
  flex-basis: 30%;
`;
const ItemActionMobile = styled.div`
  flex-basis: 100%;
`;

const ActionButton = styled.a`
  background: #ffffff;
  color: #000000;
  border-radius: 40px;
  height: 38px;
  padding: 20px 36px 20px 36px;
  justify-content: space-between;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 300;
  cursor: pointer;
  margin-top: -15px;

  box-shadow: 0px 4px 12px 4px rgba(255, 255, 255, 0.15);

  &:hover {
    transform: scale(1.05);
    box-shadow-color: rgba(255, 255, 255, 0.25);
  }
  transition: transform 0.2s ease-in-out;
`;
